import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { pub, errMsg } from "@/lib/api";
import { Logo } from "./TopBar";

const FIELDS = [
  ["name", "Your name", "text", true],
  ["org_name", "Hospital / clinic / pharmacy name", "text", true],
  ["town", "Town", "text", false],
  ["state", "State", "text", false],
  ["phone", "Phone", "tel", true],
  ["email", "Email", "email", false],
];
const input = "w-full rounded-2xl border border-[var(--lav-3)] bg-white px-4 py-3 text-[var(--ink)] outline-none transition-colors focus:border-[var(--violet)]";

export function DemoForm() {
  const [form, setForm] = useState({ preferred_language: "Telugu", patients_per_month: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await pub.post("/public/demo-requests", form);
      setDone(true);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="demo" className="bg-[var(--lav)] px-5 py-20 sm:px-8 lg:py-28" data-testid="section-demo">
      <div className="mx-auto grid max-w-6xl gap-10 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.22em] text-[var(--orange)]">Request a demo</p>
          <h2 className="mt-3 text-3xl font-extrabold tracking-tight text-[var(--ink)] sm:text-4xl">See OnDose with your own patients' medicines.</h2>
          <p className="mt-4 text-[var(--ink)]/70">We'll show you the call, the prescription scan and the doctor report in 20 minutes.</p>
          <ul className="mt-8 grid gap-3 text-sm text-[var(--ink)]/80">
            <li className="rounded-2xl bg-white px-4 py-3">Calls only repeat what your staff confirm. No medical advice.</li>
            <li className="rounded-2xl bg-white px-4 py-3">Patients join only with consent, and can stop calls any time by pressing 9.</li>
          </ul>
        </div>
        {done ? (
          <div className="grid place-items-center rounded-3xl bg-white p-10 text-center" data-testid="demo-form-success">
            <CheckCircle2 className="text-[var(--orange)]" size={48} />
            <h3 className="mt-4 text-2xl font-bold text-[var(--ink)]">Thank you!</h3>
            <p className="mt-2 text-[var(--ink)]/70">We received your request and will call you soon.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="grid gap-4 rounded-3xl bg-white p-6 sm:grid-cols-2 sm:p-8" data-testid="demo-form">
            {FIELDS.map(([k, label, type, req]) => (
              <label key={k} className="grid gap-1.5 text-sm font-medium text-[var(--ink)]">
                {label}{req && " *"}
                <input className={input} type={type} required={req} value={form[k] || ""} onChange={set(k)} data-testid={`demo-input-${k}`} />
              </label>
            ))}
            <label className="grid gap-1.5 text-sm font-medium text-[var(--ink)]">
              Preferred language
              <select className={input} value={form.preferred_language} onChange={set("preferred_language")} data-testid="demo-input-language">
                {["Telugu", "Hindi", "Tamil", "Kannada", "English", "Other"].map((l) => <option key={l}>{l}</option>)}
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-[var(--ink)]">
              Patients per month
              <select className={input} value={form.patients_per_month} onChange={set("patients_per_month")} data-testid="demo-input-patients">
                {["", "Under 100", "100–500", "500–2,000", "2,000+"].map((l) => <option key={l} value={l}>{l || "Choose"}</option>)}
              </select>
            </label>
            <label className="grid gap-1.5 text-sm font-medium text-[var(--ink)] sm:col-span-2">
              Message
              <textarea className={`${input} min-h-[110px]`} value={form.message} onChange={set("message")} data-testid="demo-input-message" />
            </label>
            <button disabled={busy} className="btn-orange inline-flex items-center justify-center gap-2 rounded-2xl px-6 py-3.5 font-semibold sm:col-span-2" data-testid="demo-form-submit">
              {busy && <Loader2 className="animate-spin" size={18} />} Request a demo
            </button>
          </form>
        )}
      </div>
    </section>
  );
}

export function Footer() {
  return (
    <footer className="bg-[var(--plum)] px-5 py-12 text-[var(--cream)] sm:px-8" data-testid="site-footer">
      <div className="mx-auto flex max-w-6xl flex-col gap-6 md:flex-row md:items-center">
        <Logo />
        <p className="text-sm text-[var(--cream)]/60 md:ml-6">Medicine reminder calls in Indian languages. Calls only repeat what is entered and confirmed.</p>
        <nav className="flex gap-5 text-sm md:ml-auto">
          <a href="mailto:hello@ondose.in" className="hover:text-[var(--orange)]" data-testid="footer-contact">hello@ondose.in</a>
          <a href="/privacy" className="hover:text-[var(--orange)]" data-testid="footer-privacy">Privacy Policy</a>
          <a href="/terms" className="hover:text-[var(--orange)]" data-testid="footer-terms">Terms</a>
        </nav>
      </div>
      <p className="mx-auto mt-8 max-w-6xl text-xs text-[var(--cream)]/40" data-testid="footer-copyright">© 2026 OnDose. All sample calls use made-up names.</p>
    </footer>
  );
}
